import type { FC } from 'react';
import { useEffect } from 'react';
import { useLazyQueryPromiseInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryPromiseInterop.ts';
import {
	ContentUnifiedQuery,
	ContentBlogUnifiedQuery,
	type ContentUnifiedQueryType,
	type ContentBlogUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
	type ContentBlogUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useHistory } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useContentType } from '@confluence/page-context';

type ContentRefetchHandlerProps = {
	contentId?: string;
	shouldRefetch: boolean;
	refetchImmediately?: boolean;
	onRefetchComplete?: (versionNumber?: number | null) => void;
};

type ChooseContentUnifiedQueryType = ContentUnifiedQueryType | ContentBlogUnifiedQueryType;
type ChooseContentUnifiedQueryVariablesType =
	| ContentUnifiedQueryVariablesType
	| ContentBlogUnifiedQueryVariablesType;

export const ContentRefetchHandler: FC<ContentRefetchHandlerProps> = ({
	contentId,
	shouldRefetch,
	refetchImmediately,
	onRefetchComplete,
}) => {
	const history = useHistory();
	const [contentType] = useContentType();
	const spaceKey = useSpaceKey();
	const chooseContentUnifiedQuery =
		contentType === 'blogpost' ? ContentBlogUnifiedQuery : ContentUnifiedQuery;

	const executeQuery = useLazyQueryPromiseInterop<
		ChooseContentUnifiedQueryType,
		ChooseContentUnifiedQueryVariablesType
	>(chooseContentUnifiedQuery, { fetchPolicy: 'network-only' });

	useEffect(() => {
		let unlisten;
		if (shouldRefetch && contentId) {
			const queryVariable: ChooseContentUnifiedQueryVariablesType = {
				contentId,
				spaceKey,
				useNewContentTopper: true,
			};

			const refetchFn = async () => {
				let versionNumber: number | null | undefined;
				try {
					const queryResult = await executeQuery({
						context: { single: true },
						variables: queryVariable,
					});

					// at this time, queryResult.data is always ContentUnifiedQueryType or ContentBlogUnifiedQueryType format
					// @ts-ignore
					versionNumber = queryResult.content?.nodes?.[0]?.version?.number;
				} catch {
					// NOTE: We intentionally swallow any possible error here as this query is only aimed to
					// populate the cache with just published content. Should any error occur here after
					// successful publishing the page, the Apollo cache for the query will be invalidated
					// anyway and View Page will fetch the page and handle possible errors.
				}

				// If a callback is supplied, call it regardless
				onRefetchComplete && onRefetchComplete(versionNumber);
			};

			if (refetchImmediately) {
				void refetchFn();
			} else {
				unlisten = history?.listen(refetchFn);
			}
		}

		return () => {
			// Call the unlistener when this component unmounts
			unlisten && unlisten();
		};
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [shouldRefetch, executeQuery]);
	return null;
};
